import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import SelectVehicleStep from '../../4_Templates/SelectVehicleStep/SelectVehicleStep'
import { languageController } from '../../../controllers/language/languageController'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import { convertLangCodeToSingleLetter, displayOutsideComponents, hideOutsideComponents } from '../../../utils/helpers'
import SelectCurrentVehicleStep from './SelectCurrentVehicleStep/SelectCurrentVehicleStep'
import YourInformationStep from './YourInformationStep/YourInformationStep'
import FindDealerStep from './FindDealerStep/FindDealerStep'
import ValueEstimateStep from './ValueEstimateStep/ValueEstimateStep'

function TradeInEstimator({ formExtraParams }) {
  const { source, language, lastStepBottomContents, initStepTopContents, buildAndPriceCtaLink, introductionText } = formExtraParams
  const maxStepNum = 5;
  const initStep = 1;

  const [currentStep, setCurrentStep] = useState(initStep)
  const [currentProgress, setCurrentProgress] = useState(initStep)
  const [data, setData] = useState({})

  // Display top contents on the init step.
  useEffect(() => {
    if (currentStep === initStep) {
      displayOutsideComponents(initStepTopContents)
    }
  }, [])

  const handleNextStep = (newProps) => {
    if (currentStep === maxStepNum) return;

    const nextStep = currentStep + 1;

    // Manage visibility of components outside of the react app.
    if (nextStep === initStep + 1) {
      // Hide TiE Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display TiE related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
    }

    setCurrentStep(nextStep)

    setData({ ...data, ...newProps })

    setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)

    window.scrollTo(0, 0)
  }

  // Go back to the init step
  const refreshSteps = () => {
    setCurrentStep(initStep)
    displayOutsideComponents(initStepTopContents)
    hideOutsideComponents(lastStepBottomContents)
  }

  return (
    <div className="mz-trade-in-estimator mz-page-top" data-current-step={currentStep} >
      <FormStepsNav
        isActive={(currentStep < maxStepNum)}
        currentStep={currentStep}
        currentProgress={currentProgress}
        setCurrentStep={nextStep => {
          // If selected step is init step, show init top contents.
          if (nextStep === initStep) {
            displayOutsideComponents(initStepTopContents)
          }
          // If selected step is greater than init step, hide init top contents.
          else if (nextStep > initStep) {
            hideOutsideComponents(initStepTopContents)
          }
          setCurrentStep(nextStep)
        }}
        navList={[
          {
            step: 1,
            label: languageController.getCopy('tradeInEstimator', 'step1'),
          },
          {
            step: 2,
            label: languageController.getCopy('tradeInEstimator', 'step2'),
          },
          {
            step: 3,
            label: languageController.getCopy('tradeInEstimator', 'step3'),
          },
          {
            step: 4,
            label: languageController.getCopy('tradeInEstimator', 'step4'),
          },
        ]}
      />
      <div className="mz-trade-in-estimator__steps">
        {/* Step 1: Select current vehicle */}
        <StepWrapper
          className={`mz-trade-in-estimator__select-current-vehicle`}
          stepNum={1}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <SelectCurrentVehicleStep handleNextStep={handleNextStep} topText={introductionText} />
        </StepWrapper>
        {/* Step 2: Select a desired vehicle */}
        <StepWrapper
          className={`mz-trade-in-estimator__select-desired-vehicle`}
          type="select-dealer"
          stepNum={2}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <SelectVehicleStep
            handleNextStep={handleNextStep}
            title={languageController.getCopy('tradeInEstimator', 'step2Title')}
          />
        </StepWrapper>
        {/* Step 3: Enter user's information */}
        <StepWrapper
          className={`mz-trade-in-estimator__user-information`}
          stepNum={3}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <YourInformationStep
            language={language}
            handleNextStep={handleNextStep}
          />
        </StepWrapper>
        {/* Step 4: Select a dealer */}
        <StepWrapper
          className={`mz-trade-in-estimator__select-dealer`}
          stepNum={4}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <FindDealerStep
            handleNextStep={handleNextStep}
            currentVehicleData={data.currentVehicleData}
            desiredVehicleData={data.vehicleData}
            userInformation={data.userInformation}
            source={source}
            language={convertLangCodeToSingleLetter(language)}
          />
        </StepWrapper>
        {/* Step 5: Show value estimate */}
        <StepWrapper
          className={`mz-trade-in-estimator__value-estimate`}
          stepNum={5}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ValueEstimateStep
            currentVehicleData={data.currentVehicleData}
            desiredVehicleData={data.vehicleData}
            refreshSteps={refreshSteps}
            language={language}
            buildAndPriceCtaLink={buildAndPriceCtaLink}
          />
        </StepWrapper>
      </div>
    </div>
  )
}

TradeInEstimator.propTypes = {
  formExtraParams: PropTypes.object.isRequired
}

TradeInEstimator.defaultProps = {
  formExtraParams: {}
}

export default TradeInEstimator